import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Stack,
} from '@mui/material';
import { Edit as EditIcon, PersonAdd as ProvisionIcon, CheckCircle as ApproveIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { DetailPageHeader, DetailTwoColumnGrid } from '../../components/common/DetailPageLayout';
import { previewProvisionedEmail } from '../../utils/provisioningPreview';
import {
  getPreviewDomainString,
  getEffectiveDomainId,
  initialPatternAndDomainIds,
} from '../../utils/provisionDomainSelection';

const EMPLOYMENT_TYPE_LABELS = {
  fulltime: 'Full time',
  parttime: 'Part time',
  contractor: 'Contractor',
  intern: 'Intern',
};

function formatDateOnly(value) {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
}

function formatMoneyAmount(amount, currencyCode) {
  if (amount == null || amount === '') return null;
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  const cur = currencyCode && String(currencyCode).trim().toUpperCase();
  if (cur) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(n);
    } catch {
      return `${cur} ${n.toLocaleString()}`;
    }
  }
  return n.toLocaleString();
}

function salaryPeriodSuffix(period) {
  if (period === 'monthly') return ' · monthly';
  if (period === 'yearly') return ' · yearly';
  return '';
}

function formatContactAddress(c) {
  if (!c) return '—';
  const lines = [];
  if (c.address_line1) lines.push(c.address_line1);
  if (c.address_line2) lines.push(c.address_line2);
  const cityState = [c.city, c.state].filter(Boolean).join(', ');
  if (cityState) lines.push(cityState);
  const zipCountry = [c.zip_code, c.country].filter(Boolean).join(' ');
  if (zipCountry) lines.push(zipCountry);
  return lines.length ? lines.join('\n') : '—';
}

function DetailField({ label, children, xs = 12, sm = 6 }) {
  return (
    <Grid item xs={xs} sm={sm}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
        {children === undefined || children === null || children === '' ? '—' : children}
      </Typography>
    </Grid>
  );
}

const EmployeeDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromMyTeam = location.pathname.startsWith('/my-team/employees');
  const listBackPath = fromMyTeam ? '/my-team/reportees' : '/employees';
  const { id } = useParams();
  const { isAdmin } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  const [postApproveOpen, setPostApproveOpen] = useState(false);
  const [postApproveBusy, setPostApproveBusy] = useState(false);
  const [approveBusy, setApproveBusy] = useState(false);

  const [provOpen, setProvOpen] = useState(false);
  const [provDomains, setProvDomains] = useState([]);
  const [provPatterns, setProvPatterns] = useState([]);
  const [provDomainId, setProvDomainId] = useState('');
  const [provPatternId, setProvPatternId] = useState('');
  const [emailOverride, setEmailOverride] = useState('');
  const [updateContactEmail, setUpdateContactEmail] = useState(true);
  const [provBusy, setProvBusy] = useState(false);
  const [provError, setProvError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!id) {
        setLoading(false);
        setEmployee(null);
        return;
      }
      setLoading(true);
      setEmployee(null);
      try {
        const r = await api.get(`/employees/${id}`);
        const data = r.data?.data;
        if (cancelled) return;
        if (!data) {
          toast.error('Employee data was missing from the server response.');
          navigate(listBackPath, { replace: true });
          return;
        }
        setEmployee(data);
      } catch (e) {
        if (!cancelled) {
          const msg = e.response?.data?.message || 'Failed to load employee';
          toast.error(msg);
          navigate(listBackPath, { replace: true });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [id, navigate, listBackPath]);

  /** Reload current employee after mutations (no full-page spinner). */
  const refetchEmployee = useCallback(async () => {
    if (!id) return;
    try {
      const r = await api.get(`/employees/${id}`);
      const data = r.data?.data;
      if (data) setEmployee(data);
    } catch {
      /* keep existing data on silent refresh failure */
    }
  }, [id]);

  const openProvision = async () => {
    setProvError('');
    setEmailOverride('');
    setUpdateContactEmail(true);
    setProvOpen(true);
    try {
      const r = await api.get('/admin/provisioning-reference');
      const d = r.data?.data?.domains || [];
      const p = r.data?.data?.patterns || [];
      setProvDomains(d);
      setProvPatterns(p);
      const { patternId, domainId } = initialPatternAndDomainIds(p, d);
      setProvPatternId(patternId);
      setProvDomainId(domainId);
    } catch {
      toast.error('Could not load domains and patterns. Check admin settings and your permissions.');
      setProvDomains([]);
      setProvPatterns([]);
    }
  };

  const contactType = employee?.Contact?.contact_type;
  const canProvisionLogin = Boolean(
    contactType && ['employee', 'contractor', 'contractor_independent', 'contractor_c2c'].includes(contactType)
  );

  const access = employee?.access || {};
  const canEdit = Boolean(access.can_edit ?? isAdmin);
  const canApprove = Boolean(access.can_approve);
  const canProvisionButton = Boolean(
    access.can_provision_login != null
      ? access.can_provision_login
      : !employee?.User && isAdmin && canProvisionLogin
  );

  const handleApprove = async () => {
    if (
      !window.confirm(
        'Approve this employee? Their HR status becomes Active and they will appear in organization employee pickers.'
      )
    ) {
      return;
    }
    setApproveBusy(true);
    try {
      await api.post(`/employees/${id}/approve`);
      toast.success('Employee approved');
      await refetchEmployee();
      setPostApproveOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Approve failed');
    } finally {
      setApproveBusy(false);
    }
  };

  const createBasicUserNow = async () => {
    setPostApproveBusy(true);
    try {
      await api.post(`/employees/${id}/create-basic-user`);
      toast.success('User account created and linked.');
      setPostApproveOpen(false);
      await refetchEmployee();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not create user');
    } finally {
      setPostApproveBusy(false);
    }
  };

  const selectedPattern = provPatterns.find((x) => x.id === provPatternId);
  const preview = useMemo(() => {
    if (!selectedPattern || !employee?.Contact) return emailOverride.trim() || '—';
    if (emailOverride.trim()) return emailOverride.trim();
    const domStr = getPreviewDomainString(provDomains, provDomainId, selectedPattern);
    if (!domStr) return '—';
    return previewProvisionedEmail(
      selectedPattern.pattern_template,
      employee.Contact.first_name,
      employee.Contact.last_name,
      domStr,
      selectedPattern
    );
  }, [selectedPattern, employee, provDomains, provDomainId, emailOverride]);

  const handleProvPatternChange = (e) => {
    const pid = e.target.value;
    setProvPatternId(pid);
    const pat = provPatterns.find((x) => x.id === pid);
    const defId = pat?.default_domain_id || pat?.defaultDomain?.id;
    if (defId && provDomains.some((d) => d.id === defId)) {
      setProvDomainId(defId);
    }
  };

  const submitProvision = async () => {
    setProvError('');
    if (!provPatternId) {
      setProvError('Select a pattern.');
      return;
    }
    const pat = provPatterns.find((x) => x.id === provPatternId);
    const effDomain = getEffectiveDomainId(provDomains, provDomainId, pat);
    if (!effDomain) {
      setProvError('Select a company domain, or set a default domain on the pattern.');
      return;
    }
    if (!canProvisionLogin) {
      setProvError('This contact must be employee or contractor to provision a login.');
      return;
    }
    setProvBusy(true);
    try {
      await api.post(`/employees/${id}/provision-login`, {
        domain_id: effDomain,
        pattern_id: provPatternId,
        email_override: emailOverride.trim() || undefined,
        update_contact_email: updateContactEmail,
      });
      toast.success('User account created. Welcome email sent if configured.');
      setProvOpen(false);
      refetchEmployee();
    } catch (e) {
      setProvError(e.response?.data?.message || 'Provisioning failed.');
    } finally {
      setProvBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!employee) {
    return (
      <Box sx={{ p: 3, maxWidth: 560 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          This employee could not be loaded (invalid link, no permission, or the record was removed).
        </Alert>
        <Button variant="contained" onClick={() => navigate(listBackPath)}>
          Back to list
        </Button>
      </Box>
    );
  }

  const typeColors = { fulltime: '#10b981', parttime: '#3b82f6', contractor: '#f59e0b', intern: '#8b5cf6' };
  const statusColors = {
    pending: '#6366f1',
    active: '#10b981',
    inactive: '#6b7280',
    terminated: '#ef4444',
    resigned: '#f59e0b',
  };
  const et = employee.employment_type;
  const hrStatus = employee.status;
  const editHref = fromMyTeam ? `/my-team/employees/${id}/edit` : `/employees/${id}/edit`;

  return (
    <Box>
      <DetailPageHeader
        title="Employee Details"
        onBack={() => navigate(listBackPath)}
        actions={
          <Stack direction="row" spacing={1}>
            {canApprove && (
              <Button
                variant="contained"
                color="success"
                startIcon={approveBusy ? <CircularProgress size={18} color="inherit" /> : <ApproveIcon />}
                onClick={handleApprove}
                disabled={approveBusy}
              >
                Approve
              </Button>
            )}
            {canProvisionButton && (
              <Button variant="outlined" startIcon={<ProvisionIcon />} onClick={openProvision}>
                Create system login
              </Button>
            )}
            {canEdit ? (
              <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(editHref)}>
                Edit
              </Button>
            ) : null}
          </Stack>
        }
      />

      <DetailTwoColumnGrid
        sidebar={
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Avatar
                sx={{ width: 100, height: 100, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontSize: '2rem' }}
              >
                {employee.Contact?.first_name?.[0]}
                {employee.Contact?.last_name?.[0]}
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {employee.Contact?.first_name} {employee.Contact?.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {employee.job_title || employee.designation || 'N/A'}
              </Typography>
              {(employee.Contact?.employeeJobRole?.name || employee.Contact?.employeeTitle?.name) && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {[employee.Contact?.employeeJobRole?.name, employee.Contact?.employeeTitle?.name]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              )}
              {(employee.team_name || employee.department) && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {[employee.team_name, employee.department].filter(Boolean).join(' · ')}
                </Typography>
              )}
              <Chip label={employee.employee_code} size="small" sx={{ mt: 1, fontFamily: 'monospace', fontWeight: 600 }} />
              <Chip
                label={et?.toUpperCase()}
                size="small"
                sx={{
                  mt: 1,
                  ml: 1,
                  bgcolor: `${typeColors[et]}20`,
                  color: typeColors[et],
                }}
              />
              <Chip
                label={`HR: ${String(hrStatus || '—').toUpperCase()}`}
                size="small"
                sx={{
                  mt: 1,
                  ml: 1,
                  bgcolor: `${statusColors[hrStatus] || '#64748b'}20`,
                  color: statusColors[hrStatus] || '#64748b',
                  fontWeight: 600,
                  fontSize: '0.65rem',
                }}
              />
              <Divider sx={{ my: 3 }} />
              <Typography variant="body2" color="text.secondary">
                Department
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {employee.department || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Joined
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {employee.date_of_joining ? new Date(employee.date_of_joining).toLocaleDateString() : 'N/A'}
              </Typography>
              {(employee.reportingManager || employee.manager) && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Reporting manager
                  </Typography>
                  <Typography variant="body1">
                    {employee.reportingManager
                      ? `${employee.reportingManager.first_name || ''} ${employee.reportingManager.last_name || ''}`.trim() ||
                        employee.reportingManager.email
                      : `${employee.manager?.Contact?.first_name || ''} ${employee.manager?.Contact?.last_name || ''}`.trim()}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        }
      >
        <Card>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tab label="Overview" />
            <Tab label={`Team (${employee.reportees?.length || 0})`} />
          </Tabs>
          <CardContent>
            {activeTab === 0 && (
              <Grid container spacing={3}>
                {hrStatus === 'pending' && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      This HR profile is <strong>pending approval</strong>. They do not appear in organization employee
                      pickers until an administrator or their reporting manager approves. Use Edit to update the profile
                      before approving.
                    </Alert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Employment
                  </Typography>
                  <Grid container spacing={2}>
                    <DetailField label="Employment type">
                      {EMPLOYMENT_TYPE_LABELS[et] || et || '—'}
                    </DetailField>
                    <DetailField label="HR status">{String(hrStatus || '—').replace(/_/g, ' ')}</DetailField>
                    <DetailField label="Date of joining">{formatDateOnly(employee.date_of_joining)}</DetailField>
                    <DetailField label="Date of leaving">{formatDateOnly(employee.date_of_leaving)}</DetailField>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Position &amp; organization
                  </Typography>
                  <Grid container spacing={2}>
                    <DetailField label="Job title (HR record)">{employee.job_title}</DetailField>
                    <DetailField label="Designation / role code">{employee.designation}</DetailField>
                    <DetailField label="Department">{employee.department}</DetailField>
                    <DetailField label="Team name">{employee.team_name}</DetailField>
                    <DetailField label="Company associated (hosting company)">
                      {employee.clientAccount
                        ? `${employee.clientAccount.company_name}${employee.clientAccount.account_code ? ` (${employee.clientAccount.account_code})` : ''}`
                        : '—'}
                    </DetailField>
                    <DetailField label="Linked job requirement">
                      {employee.assignedJob
                        ? `${employee.assignedJob.job_code} — ${employee.assignedJob.title}`
                        : '—'}
                    </DetailField>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Employee workplace (contact catalog)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Employee job role
                      </Typography>
                      <Typography variant="body2">{employee.Contact?.employeeJobRole?.name || '—'}</Typography>
                      {employee.Contact?.employeeJobRole?.description && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {employee.Contact.employeeJobRole.description}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Employee level
                      </Typography>
                      <Typography variant="body2">{employee.Contact?.employeeTitle?.name || '—'}</Typography>
                      {employee.Contact?.employeeTitle?.description && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {employee.Contact.employeeTitle.description}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Employee offered title
                      </Typography>
                      <Typography variant="body2">{employee.Contact?.employeeOfferedTitle?.name || '—'}</Typography>
                    </Grid>
                    <DetailField label="Payroll / external employee ID">{employee.Contact?.external_employee_id}</DetailField>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Reporting
                  </Typography>
                  <Grid container spacing={2}>
                    <DetailField label="Reporting manager" xs={12} sm={12}>
                      {employee.reportingManager
                        ? `${employee.reportingManager.first_name || ''} ${employee.reportingManager.last_name || ''}`.trim() ||
                          employee.reportingManager.email
                        : employee.manager?.Contact
                          ? `${employee.manager.Contact.first_name || ''} ${employee.manager.Contact.last_name || ''}`.trim() ||
                            employee.manager.Contact.email
                          : '—'}
                    </DetailField>
                    <DetailField label="Alternate reporting manager" xs={12} sm={12}>
                      {employee.Contact?.alternateReportingManagerContact
                        ? `${employee.Contact.alternateReportingManagerContact.first_name || ''} ${employee.Contact.alternateReportingManagerContact.last_name || ''}`.trim() ||
                          employee.Contact.alternateReportingManagerContact.email
                        : '—'}
                    </DetailField>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Linked contact
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Button size="small" variant="outlined" component={RouterLink} to={`/contacts/${employee.contact_id}`}>
                        Open contact record
                      </Button>
                    </Grid>
                    <DetailField label="Name">
                      {`${employee.Contact?.first_name || ''} ${employee.Contact?.last_name || ''}`.trim() || '—'}
                    </DetailField>
                    <DetailField label="Contact type">{employee.Contact?.contact_type}</DetailField>
                    <DetailField label="Contact status">{employee.Contact?.status}</DetailField>
                    <DetailField label="Email">{employee.Contact?.email}</DetailField>
                    {employee.User && (
                      <DetailField label="System login (user)">{employee.User.email}</DetailField>
                    )}
                    {!employee.User && !canProvisionLogin && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          System login can be created only when the linked contact type is employee or contractor.
                        </Typography>
                      </Grid>
                    )}
                    <DetailField label="Phone">{employee.Contact?.phone}</DetailField>
                    <DetailField label="LinkedIn">{employee.Contact?.linkedin_url}</DetailField>
                    <DetailField label="Address" xs={12}>
                      {formatContactAddress(employee.Contact)}
                    </DetailField>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Compensation
                  </Typography>
                  <Grid container spacing={2}>
                    <DetailField label="Salary (HR profile)">
                      {employee.salary != null && employee.salary !== ''
                        ? `${formatMoneyAmount(employee.salary, employee.salary_currency)}${salaryPeriodSuffix(
                            employee.salary_period
                          )}`
                        : '—'}
                    </DetailField>
                    <DetailField label="Hourly rate">
                      {employee.hourly_rate != null && employee.hourly_rate !== ''
                        ? `${formatMoneyAmount(employee.hourly_rate, employee.hourly_currency) || '—'}/hr`
                        : '—'}
                    </DetailField>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Last login provisioning
                  </Typography>
                  <Grid container spacing={2}>
                    <DetailField label="Company domain">
                      {employee.lastProvisionDomain
                        ? `${employee.lastProvisionDomain.company_name} (${employee.lastProvisionDomain.domain})`
                        : '—'}
                    </DetailField>
                    <DetailField label="User ID pattern">{employee.lastProvisionPattern?.name}</DetailField>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    HR notes
                  </Typography>
                  <DetailField label="Notes" xs={12} sm={12}>
                    {employee.notes}
                  </DetailField>
                </Grid>

                {employee.recentTimesheets?.length > 0 && (
                  <Grid item xs={12}>
                    <Divider />
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                      Recent timesheets
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Last {employee.recentTimesheets.length} period(s)
                    </Typography>
                    <List dense disablePadding>
                      {employee.recentTimesheets.map((ts) => (
                        <ListItem key={ts.id} disableGutters sx={{ py: 0.25 }}>
                          <ListItemText
                            primary={`${formatDateOnly(ts.period_start)} – ${formatDateOnly(ts.period_end)}`}
                            secondary={ts.timesheet_type ? String(ts.timesheet_type) : undefined}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                )}
              </Grid>
            )}
            {activeTab === 1 && (
              <List>
                {!employee.reportees?.length ? (
                  <Typography color="text.secondary" textAlign="center" py={4}>
                    No direct reports
                  </Typography>
                ) : (
                  employee.reportees.map((r) => (
                    <ListItem
                      key={r.id}
                      divider
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(fromMyTeam ? `/my-team/employees/${r.id}` : `/employees/${r.id}`)}
                    >
                      <ListItemText
                        primary={`${r.Contact?.first_name} ${r.Contact?.last_name}`}
                        secondary={[r.employee_code, r.designation].filter(Boolean).join(' · ') || undefined}
                      />
                    </ListItem>
                  ))
                )}
              </List>
            )}
          </CardContent>
        </Card>
      </DetailTwoColumnGrid>

      <Dialog open={postApproveOpen} onClose={() => !postApproveBusy && setPostApproveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Employee approved</DialogTitle>
        <DialogContent>
          {isAdmin ? (
            <>
              <Typography variant="body2" color="text.secondary">
                Do you also want to create a <strong>basic system user</strong> now (email + temporary password, Employees
                group)? You can skip this and add a user later from <strong>Add user</strong> or use{' '}
                <strong>Create system login</strong> for domain-based provisioning.
              </Typography>
              {!access.can_create_basic_user && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  The linked contact needs a valid email (or a user already exists) before a basic user can be created
                  from this prompt. Use <strong>Edit</strong> to add an email, or create the user later from{' '}
                  <strong>Add user</strong>.
                </Alert>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              This employee is now <strong>Active</strong>. Only an administrator can create a system user from User
              management or from this profile.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {isAdmin ? (
            <>
              <Button onClick={() => setPostApproveOpen(false)} disabled={postApproveBusy}>
                Not now
              </Button>
              <Button
                variant="contained"
                onClick={createBasicUserNow}
                disabled={postApproveBusy || !access.can_create_basic_user}
              >
                {postApproveBusy ? <CircularProgress size={22} /> : 'Yes, create user'}
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={() => setPostApproveOpen(false)}>
              OK
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={provOpen} onClose={() => !provBusy && setProvOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create system login</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pick the company email domain and the user ID pattern. The preview is the login email that will be created
            (same rules as on save). Confirm it, then create user — they can sign in with that address and the welcome
            password flow. Contact must be type employee or contractor.
          </Typography>
          {provError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {provError}
            </Alert>
          )}
          {(!provDomains.length || !provPatterns.length) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {!provDomains.length && 'Add at least one company domain under Administrator → Company Domains. '}
              {!provPatterns.length && 'Add at least one user ID pattern under Administrator → User ID Pattern.'}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="User ID pattern"
              fullWidth
              size="small"
              value={provPatternId}
              onChange={handleProvPatternChange}
            >
              {provPatterns.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                  {(p.default_domain_id || p.defaultDomain?.id) ? ' · has default domain' : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Company domain (optional if pattern has default)"
              fullWidth
              size="small"
              value={provDomainId}
              onChange={(e) => setProvDomainId(e.target.value)}
              helperText={
                selectedPattern &&
                (selectedPattern.default_domain_id || selectedPattern.defaultDomain?.id) &&
                getEffectiveDomainId(provDomains, provDomainId, selectedPattern)
                  ? 'Pre-filled from the pattern; change here to use another domain.'
                  : undefined
              }
            >
              {provDomains.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.company_name} — {d.domain}
                  {d.is_primary ? ' (primary)' : ''}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="text.secondary">
              Preview
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {preview}
            </Typography>
            <TextField
              label="Override email (optional)"
              fullWidth
              size="small"
              value={emailOverride}
              onChange={(e) => setEmailOverride(e.target.value)}
              helperText="If set, this full address is used instead of the pattern preview (must be unique)."
            />
            <FormControlLabel
              control={
                <Checkbox checked={updateContactEmail} onChange={(e) => setUpdateContactEmail(e.target.checked)} />
              }
              label="Also set contact email to this login address"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProvOpen(false)} disabled={provBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitProvision}
            disabled={
              provBusy ||
              !provDomains.length ||
              !provPatterns.length ||
              !getEffectiveDomainId(provDomains, provDomainId, selectedPattern)
            }
          >
            {provBusy ? <CircularProgress size={22} /> : 'Create user'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeDetails;
